/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, Component, Inject, NgZone, OnInit} from '@angular/core';
import {StateService, UIRouter, UrlRouter} from '@uirouter/core';
import {TranslateService} from '@ngx-translate/core';
import {DomSanitizer} from '@angular/platform-browser';
import * as _ from 'lodash';

import template from './sign-in-page.component.html';
import {AdnTranslateService} from '../common/filters/adn-translate.service';
import {SignInFormBase} from './sign-in-form.base';
import {AdnAuthHelperService} from '../../components/util/adn-auth-helper.service';
import {AdnLoginNavigationService} from '../../components/util/adn-login-navigation.service';
import {AuthService} from '../../components/auth/auth.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {WebAuthnService} from '../common/controller/web-authn.service';
import {BrowserLocationService, WINDOW} from '../angular-platform-providers';
import {ExceptionHandlerService} from '../errors/exception-handler.service';
import {AuthHttpBufferService} from '../../components/auth/auth-http-buffer.service';
import {DocumentShellService} from '../base/document-shell.service';
import {AppEventBusService} from '../common/events/app-event-bus.service';
import {AuthReturnNavigationService} from './auth-return-navigation.service';
import {BuildReloadService} from '../base/build-reload.service';
import {isOAuthConsentDestination} from '../admin/api-key/api-key-commons';
import {ApiConfig} from '../config/api-config';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {FormsModule} from '@angular/forms';
import {UIRouterModule} from '@uirouter/angular';
import {AppSharedModule} from '../common/app-shared.module';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, UIRouterModule],
  selector: 'adn-sign-in-page',
  template
})
export class SignInPageComponent extends SignInFormBase implements OnInit {
  private static readonly POST_AUTH_BUSY_MS = 300;
  aiAccessSignIn = false;

  constructor(
    ngZone: NgZone,
    sanitizer: DomSanitizer,
    adnTranslate: AdnTranslateService,
    @Inject(WINDOW) window: any,
    @Inject(DOCUMENT) document: any,
    eventBus: AppEventBusService,
    @Inject(BrowserLocationService) location: any,
    $state: StateService,
    uiRouter: UIRouter,
    translate: TranslateService,
    exceptionHandler: ExceptionHandlerService,
    @Inject(AuthService) AuthService: any,
    @Inject(AdnAuthHelperService) adnAuthHelper: any,
    webAuthnResource: WebAuthnService,
    @Inject(LocalUserProfile) localUserProfile: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    documentShell: DocumentShellService,
    private readonly authHttpBuffer: AuthHttpBufferService,
    @Inject(AdnLoginNavigationService) private readonly adnLoginNavigation: any,
    private readonly $urlRouter: UrlRouter,
    private readonly authReturnNavigation: AuthReturnNavigationService,
    private readonly buildReload: BuildReloadService,
    apiConfig: ApiConfig,
    localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(
      ngZone, sanitizer, adnTranslate, window, document, eventBus, location, $state, uiRouter,
      translate, exceptionHandler, AuthService, adnAuthHelper, webAuthnResource,
      localUserProfile, LocalNetworkProfile, LocalUserPermissions, documentShell, apiConfig, localNetworkInfo
    );
  }

  ngOnInit(): void {
    this.initSharedState();
    const returnNav = this.authReturnNavigation.get();
    this.aiAccessSignIn = isOAuthConsentDestination(returnNav?.toState, returnNav?.toParams);
    this.leaveIfAlreadyAuthenticated();
    setTimeout(() => {
      this.window.adn = this.window.adn || {};
      this.window.adn.calls = this.window.adn.calls || [];
      this.window.adn.calls.push(() => {
        this.window.adn.request({
          env: 'production',
          adUnits: [{
            targetId: 'adnuntius-ad',
            auId: '000000000009a55e'
          }]
        });
      });
    }, 20);
  }

  protected onAuthSuccess(session: any): void {
    this.authHttpBuffer.loginConfirmed(session || this.AuthService.getSessionData());
    setTimeout(() => {
      const onSuccess = () => {
        this.authReturnNavigation.clear();
        this.buildReload.reloadIfNewerBuild();
      };
      const returnNav = this.authReturnNavigation.get();
      if (returnNav && _.get(returnNav.toState, 'name') && returnNav.toState.name.indexOf('sign-in') !== 0) {
        this.adnLoginNavigation.navigateToStateOrHome(this.$state, returnNav.toState, returnNav.toParams, {})
          .then(onSuccess);
        return;
      }
      this.adnLoginNavigation.navigateAfterLogin(this.$state, this.location, this.$urlRouter, {
        dest: this.$state.params.dest,
        onSuccess: onSuccess
      });
    }, SignInPageComponent.POST_AUTH_BUSY_MS);
  }

  private leaveIfAlreadyAuthenticated(): void {
    if (this.$state.params.explicitlySignedOut) {
      return;
    }
    if (this.AuthService.isAuthenticated() && !this.AuthService.getSession().isExpired()) {
      this.adnLoginNavigation.navigateAfterLogin(this.$state, this.location, this.$urlRouter, {
        dest: this.$state.params.dest
      });
    }
  }

}
